
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useActivation } from '@/contexts/ActivationContext';
import { toast } from 'sonner';
import { httpClient } from '@/lib/http-client';
import { getEndpointUrl } from '@/lib/api-config';

interface EnrollmentResponse {
  token: string;
  urlWebpay: string;
}

interface MerchantCard {
  id: string;
  cardType: string;
  lastFour: string;
  isDefault: boolean;
  installments: number;
  createdAt: string;
  allowsInstallments: boolean;
}

const formatDate = (iso: string | null) => {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function PagoAutomaticoTarjetasPanel() {
  const { status, isStepComplete, refreshStatus } = useActivation();
  const [enrolling, setEnrolling] = useState(false);
  const [cards, setCards] = useState<MerchantCard[]>([]);
  const [cardsLoading, setCardsLoading] = useState(true);

  const isCompleted = isStepComplete('lupsActivation');
  const isSiiCompleted = isStepComplete('conexionSii');
  const completedAt = status?.lupsActivation?.completedAt ?? null;

  const loadCards = async () => {
    try {
      const url = getEndpointUrl('companyCards', 'list');
      const data = await httpClient.get<MerchantCard[]>(url, { shouldCache: false });
      setCards(data);
    } catch {
      setCards([]);
    } finally {
      setCardsLoading(false);
    }
  };

  useEffect(() => {
    void loadCards();
  }, []);

  // Si volvemos de Transbank con ?addCardStatus=..., mostrar toast + refrescar.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const addCardStatus = params.get('addCardStatus');
    if (!addCardStatus) return;
    if (addCardStatus === 'success') {
      toast.success('Tarjeta inscrita correctamente');
    } else {
      toast.error('No pudimos inscribir la tarjeta', { description: 'Intenta de nuevo en unos minutos.' });
    }
    params.delete('addCardStatus');
    const cleanUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.history.replaceState({}, '', cleanUrl);
    void refreshStatus();
    void loadCards();
  }, [refreshStatus]);

  const handleInscribir = async () => {
    setEnrolling(true);
    try {
      const url = getEndpointUrl('companyCards', 'enrollment');
      const response = await httpClient.post<EnrollmentResponse>(url, {});
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = (await response.json()) as EnrollmentResponse;
      if (!data.urlWebpay || !data.token) throw new Error('Respuesta inválida del servidor');

      // OneClick de Transbank requiere POST de un form con TBK_TOKEN al urlWebpay.
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = data.urlWebpay;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'TBK_TOKEN';
      input.value = data.token;
      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      toast.error('No pudimos iniciar la inscripción', {
        description: (err as Error).message || 'Intenta de nuevo en unos minutos.',
      });
      setEnrolling(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Pago automático con tarjeta (PAT)</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Para solicitar adelantos, necesitas inscribir al menos una tarjeta de crédito. Cada vez que venza una cuota adelantada, vamos a cobrarla automáticamente con esa tarjeta.
        </p>
      </div>

      <div className="rounded-xl border bg-card p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <span className="material-symbols-outlined rounded-lg bg-flux-primary/10 p-3 text-flux-primary">credit_card</span>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-medium">Inscripción de tarjeta (PAT)</h2>
              <span className="text-[11px] font-medium text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-400 px-2 py-0.5 rounded-full">Obligatorio</span>
            </div>
            {isCompleted ? (
              <p className="mt-1 text-sm text-accent-green">
                Completado {completedAt ? `el ${formatDate(completedAt)}` : ''}. Tienes al menos una tarjeta activa inscrita.
              </p>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">Pendiente — aún no hay una tarjeta inscrita.</p>
            )}
          </div>
        </div>

        <div className="mt-6 space-y-3 text-sm text-muted-foreground">
          <p><strong>Cómo funciona:</strong></p>
          <ol className="list-decimal space-y-1 pl-5">
            <li>Haz click en &quot;Inscribir tarjeta&quot;. Te vamos a llevar a un portal seguro de nuestro proveedor de cobros.</li>
            <li>Ingresa los datos de tu tarjeta en ese portal.</li>
            <li>Al terminar, vuelves a esta pantalla automáticamente y el estado se actualiza en unos segundos.</li>
          </ol>
          <p className="pt-2">Puedes inscribir más de una tarjeta. Basta con tener al menos una activa para completar este paso.</p>
        </div>

        {!isSiiCompleted && (
          <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-300">
            <p className="flex items-start gap-2">
              <span className="material-symbols-outlined text-base mt-0.5">info</span>
              <span>
                Antes de inscribir una tarjeta, completa la{' '}
                <Link to="/configuraciones/conexion-sii" className="font-medium underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-200">
                  conexión con el SII
                </Link>
                . Necesitamos el RUT de tu empresa para inscribir la tarjeta.
              </span>
            </p>
          </div>
        )}

        <div className="mt-6">
          <button
            type="button"
            onClick={handleInscribir}
            disabled={enrolling || !isSiiCompleted}
            title={!isSiiCompleted ? 'Completa primero la conexión con el SII para cargar el RUT' : undefined}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-flux-primary px-4 py-2 text-sm font-medium text-white hover:bg-flux-primary/90 focus:outline-none focus:ring-2 focus:ring-flux-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="material-symbols-outlined text-base">open_in_new</span>
            {enrolling ? 'Redirigiendo…' : 'Inscribir tarjeta'}
          </button>
        </div>
      </div>

      <div className="rounded-xl border bg-card p-6 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-lg font-medium">Mis tarjetas inscritas</h2>
          <span className="text-xs text-muted-foreground">{cards.length} tarjeta{cards.length === 1 ? '' : 's'}</span>
        </div>

        {cardsLoading ? (
          <p className="mt-4 text-sm text-muted-foreground">Cargando tarjetas…</p>
        ) : cards.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">Aún no hay tarjetas inscritas.</p>
        ) : (
          <ul className="mt-4 divide-y divide-border">
            {cards.map((card) => (
              <li key={card.id} className="flex items-center justify-between gap-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-[22px] text-flux-primary">credit_card</span>
                  <div>
                    <p className="text-sm font-medium">{card.cardType} ···· {card.lastFour}</p>
                    <p className="text-xs text-muted-foreground">Inscrita el {formatDate(card.createdAt)}</p>
                  </div>
                </div>
                {card.isDefault && (
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-accent-green/15 text-accent-green">Predeterminada</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
